import { parseFormula } from '../utils/formula-utils';
// ============================================================
// Mock evaluator for web preview
// ============================================================
/**
 * Evaluates a FormulaInstruction using deterministic mock data.
 *
 * Account-based formulas produce pseudo-random amounts seeded
 * by the account code. Sum and arithmetic formulas operate on
 * previously resolved values from the resolvedValues map.
 *
 * @param instruction - The formula instruction to evaluate
 * @param resolvedValues - Map of rowId → computed numeric value
 * @param columnType - The column type (used for variance calculations)
 * @param siblingValues - Map of columnType → value for the same row (for variance)
 */
export function evaluateMock(instruction, resolvedValues, columnType, siblingValues) {
    switch (instruction.type) {
        case 'clm_balance':
        case 'clm_prior_period':
        case 'clm_prior_year':
        case 'clm_budget':
        case 'clm_ytd':
        case 'clm_period_offset':
        case 'clm_year_offset': {
            const code = instruction.accountCode ?? 'unknown';
            const base = seedToAmount(code);
            // Vary by instruction type so different columns show different values
            return applyTypeVariation(base, instruction.type);
        }
        case 'sum_rows': {
            if (!instruction.rowRefs || instruction.rowRefs.length === 0)
                return 0;
            let total = 0;
            for (const ref of instruction.rowRefs) {
                const val = resolvedValues.get(ref);
                if (val !== undefined) {
                    total += val;
                }
            }
            return total;
        }
        case 'row_arithmetic': {
            if (!instruction.userFormula)
                return 0;
            return evaluateArithmeticFormula(instruction.userFormula, resolvedValues);
        }
        case 'variance_amount': {
            if (!siblingValues)
                return 0;
            const current = siblingValues.get('current') ?? 0;
            const comparison = siblingValues.get('budget') ?? siblingValues.get('prior_period') ?? 0;
            return current - comparison;
        }
        case 'variance_percent': {
            if (!siblingValues)
                return 0;
            const current = siblingValues.get('current') ?? 0;
            const comparison = siblingValues.get('budget') ?? siblingValues.get('prior_period') ?? 0;
            if (comparison === 0)
                return 0;
            return (current - comparison) / Math.abs(comparison);
        }
        case 'literal':
            return 0;
    }
}
// ============================================================
// Deterministic seed-based amount generator
// ============================================================
/**
 * Generates a deterministic dollar amount from a string seed.
 * Uses a simple hash function to produce stable values across re-renders.
 */
function seedToAmount(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash + char) | 0;
    }
    // Map hash to a range that looks like realistic financial data
    // Range: -50,000 to 500,000 (skewed positive)
    const normalized = ((hash & 0x7FFFFFFF) % 55000) / 100;
    return Math.round(normalized * 100) / 100;
}
/**
 * Applies variation based on formula type so that "Current" and "Budget"
 * columns show different but plausible values.
 */
function applyTypeVariation(base, type) {
    switch (type) {
        case 'clm_balance':
            return base;
        case 'clm_budget':
            return Math.round((base * 1.05) * 100) / 100; // Budget 5% higher
        case 'clm_prior_period':
            return Math.round((base * 0.97) * 100) / 100; // Prior period 3% lower
        case 'clm_prior_year':
            return Math.round((base * 0.92) * 100) / 100; // Prior year 8% lower
        case 'clm_ytd':
            return Math.round((base * 6) * 100) / 100; // ~6 months
        case 'clm_period_offset':
            return Math.round((base * 0.98) * 100) / 100;
        case 'clm_year_offset':
            return Math.round((base * 0.90) * 100) / 100;
        default:
            return base;
    }
}
// ============================================================
// Arithmetic formula evaluator
// ============================================================
/**
 * Evaluates a bracket-reference formula against resolved values.
 * e.g., "[10] + [20] * 2" with resolvedValues { "10": 100, "20": 50 }
 * becomes "100 + 50 * 2" = 200
 */
function evaluateArithmeticFormula(formula, resolvedValues) {
    const parsed = parseFormula(formula);
    if (!parsed.valid)
        return 0;
    // Replace bracket references with numeric values
    let expression = formula;
    for (const ref of parsed.references) {
        const val = resolvedValues.get(ref.rowId) ?? 0;
        expression = expression.replace(ref.raw, String(val));
    }
    // Evaluate the arithmetic expression safely
    return safeEval(expression);
}
/**
 * Safely evaluates a simple arithmetic expression containing
 * only numbers, +, -, *, /, parentheses, and whitespace.
 */
function safeEval(expression) {
    // Strip whitespace
    const cleaned = expression.replace(/\s/g, '');
    // Validate: only allow digits, decimal points, operators, and parens
    if (!/^[0-9+\-*/().]+$/.test(cleaned))
        return 0;
    try {
        // Use Function constructor for safe arithmetic evaluation
        // This is safe because we've validated the input contains only arithmetic
        const fn = new Function(`return (${cleaned});`);
        const result = fn();
        if (typeof result !== 'number' || !isFinite(result))
            return 0;
        return Math.round(result * 100) / 100;
    }
    catch {
        return 0;
    }
}
//# sourceMappingURL=mock-evaluator.js.map